/**
 * AttentionSupport.jsx — bundles two attention-friendly nudges that
 * live at the app-shell level:
 *
 *   1. IdleNudge  — after 60s of no interaction on an activity page,
 *      a single soft toast appears ("still there? tap to keep going").
 *      Once, then silence. PART 4 rule: "ONE NUDGE, NEVER MORE".
 *
 *   2. BreakOffer — after 15 continuous minutes since the shell
 *      mounted, offer (never force) a 30-second break card. Dismiss-
 *      able. Respects parent `breakReminders` toggle.
 *
 * Renders nothing visual by default — it's timing + toast side-effects.
 * Import once inside any focus-driven wrapper (or LessonReader / Quiz)
 * to activate.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAttentionSettings } from "@/lib/attentionSettings";
import { noteIdleNudge } from "@/lib/attentionMetrics";

/** Fires a single toast at 60s of no user activity. */
export function IdleNudge({ enabled = true }) {
  const firedRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    const reset = () => {
      if (firedRef.current) return; // one nudge, never more
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        if (firedRef.current) return;
        firedRef.current = true;
        toast("Still there? Tap to keep going.", { duration: 4000 });
        try { noteIdleNudge(); } catch { /* ignore */ }
      }, 60 * 1000);
    };
    const events = ["pointerdown", "keydown", "wheel", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [enabled]);

  return null;
}

/** Offers a 30-second break at 15 continuous minutes. */
export function BreakOffer() {
  const { breakReminders } = useAttentionSettings();
  const [visible, setVisible] = useState(false);
  const [countdown, setCountdown] = useState(30);

  useEffect(() => {
    if (!breakReminders) return undefined;
    const t = setTimeout(() => setVisible(true), 15 * 60 * 1000);
    return () => clearTimeout(t);
  }, [breakReminders]);

  useEffect(() => {
    if (!visible) return undefined;
    if (countdown <= 0) return undefined;
    const t = setTimeout(() => setCountdown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [visible, countdown]);

  if (!breakReminders || !visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Take a short break?"
      data-testid="break-offer"
      className="fixed inset-x-3 bottom-3 z-50 max-w-md mx-auto rounded-2xl border-4 border-slate-900 bg-white p-4 shadow-[6px_6px_0_0_rgba(15,23,42,1)]"
    >
      <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Optional · stretch break</p>
      <p className="mt-1 font-heading text-lg md:text-xl font-bold text-slate-900 leading-tight">
        Want a quick stretch? Look up at the ceiling and count to {countdown}.
      </p>
      <p className="mt-1 text-xs font-bold text-slate-600">Your progress is right where you left it.</p>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={() => setVisible(false)}
          data-testid="break-offer-later"
          className="flex-1 h-11 rounded-full border-[3px] border-slate-900 bg-white font-bold text-xs shadow-[0_3px_0_0_rgba(15,23,42,1)]"
        >
          Keep working
        </button>
        <button
          type="button"
          onClick={() => setVisible(false)}
          data-testid="break-offer-done"
          className="flex-1 h-11 rounded-full border-[3px] border-slate-900 bg-emerald-400 font-bold text-xs shadow-[0_3px_0_0_rgba(15,23,42,1)]"
        >
          Done stretching
        </button>
      </div>
    </div>
  );
}
